import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { employeesAPI, familyAPI, termCoveragesAPI, hrmsAPI } from '../services/api';

const RELATIONS = ['spouse', 'son', 'daughter', 'father', 'mother', 'father_in_law', 'mother_in_law'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

function formatCurrency(n) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
}

function SettlementPanel({ settlement, title }) {
  if (!settlement) return null;
  return (
    <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm space-y-2">
      <p className="font-medium text-slate-700">{title}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div><span className="text-slate-400 block">Age at enrollment</span>{settlement.ageAtEnrollment} yrs (slab {settlement.slabAtEnrollment?.ageBand})</div>
        <div><span className="text-slate-400 block">Age at exit</span>{settlement.ageAtExit} yrs (slab {settlement.slabAtExit?.ageBand})</div>
        <div><span className="text-slate-400 block">Covered days</span>{settlement.coveredDays}</div>
        <div><span className="text-slate-400 block">Unused days</span>{settlement.unusedDays}</div>
        <div><span className="text-slate-400 block">Used premium</span>{formatCurrency(settlement.usedTotal)}</div>
        <div><span className="text-slate-400 block">Refund (incl. GST)</span><span className="font-semibold text-green-700">{formatCurrency(settlement.refundTotal)}</span></div>
        <div><span className="text-slate-400 block">Net after exit</span>{formatCurrency(settlement.netPremiumAfterExit)}</div>
      </div>
    </div>
  );
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = ['super_admin', 'hr_manager'].includes(user?.role);
  const [employee, setEmployee] = useState(null);
  const [lookups, setLookups] = useState({ departments: [], branches: [], policies: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [editPreviewLoading, setEditPreviewLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [updateMessage, setUpdateMessage] = useState(null);
  const [extendingWindow, setExtendingWindow] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendReason, setExtendReason] = useState('');
  const [exitDate, setExitDate] = useState(new Date().toISOString().slice(0, 10));
  const [exitPreview, setExitPreview] = useState(null);
  const [exitLoading, setExitLoading] = useState(false);
  const [familyPreview, setFamilyPreview] = useState(null);
  const [termCoverages, setTermCoverages] = useState([]);
  const [hrmsSyncEnabled, setHrmsSyncEnabled] = useState(false);
  const [form, setForm] = useState({
    member_name: '', relation: 'spouse', date_of_birth: '', gender: 'female',
    pre_existing_disease: '', aadhaar: '',
  });

  const masterLocked = hrmsSyncEnabled;
  const load = () => {
    employeesAPI.get(id).then(({ data }) => setEmployee(data)).finally(() => setLoading(false));
    termCoveragesAPI.byEmployee(id).then(({ data }) => setTermCoverages(data)).catch(() => setTermCoverages([]));
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (canEdit) {
      employeesAPI.lookups().then(({ data }) => setLookups(data)).catch(() => {});
      hrmsAPI.config()
        .then(({ data }) => setHrmsSyncEnabled(Boolean(data.syncEnabled)))
        .catch(() => setHrmsSyncEnabled(false));
    }
  }, [canEdit]);

  const openEdit = () => {
    if (!employee) return;
    setEditForm({
      employee_code: employee.employee_code || '',
      full_name: employee.full_name || '',
      designation: employee.designation || '',
      date_of_joining: employee.date_of_joining?.slice(0, 10) || '',
      date_of_birth: employee.date_of_birth?.slice(0, 10) || '',
      gender: employee.gender || 'male',
      mobile_number: employee.mobile_number || '',
      email: employee.email || '',
      salary_ctc: employee.salary_ctc || '',
      blood_group: employee.blood_group || 'O+',
      employment_type: employee.employment_type || 'permanent',
      hrms_integration_id: employee.hrms_integration_id || '',
      department_id: String(employee.department_id || ''),
      branch_id: String(employee.branch_id || ''),
      policy_id: String(employee.policy_id || ''),
      enrollment_date: employee.enrollment_date?.slice(0, 10) || employee.date_of_joining?.slice(0, 10) || '',
      family_enrollment_window_days: employee.family_enrollment_window_days ?? 10,
      enrollment_window_override_until: employee.enrollment_window_override_until?.slice(0, 10) || '',
      enrollment_override_reason: '',
      aadhaar: '',
      pan: '',
    });
    setEditPreview(null);
    setEditError(null);
    setShowEdit(true);
  };

  useEffect(() => {
    if (!showEdit || !editForm?.policy_id || !editForm?.date_of_birth) {
      setEditPreview(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setEditPreviewLoading(true);
      setEditError(null);
      try {
        const { data } = await employeesAPI.calculatePremium({
          policy_id: parseInt(editForm.policy_id, 10),
          date_of_birth: editForm.date_of_birth,
          gender: editForm.gender,
          date_of_joining: editForm.date_of_joining,
          enrollment_date: editForm.enrollment_date,
          employment_type: editForm.employment_type,
        });
        setEditPreview(data.premium);
      } catch (err) {
        setEditPreview(null);
        setEditError(err.response?.data?.error || 'Unable to preview premium');
      } finally {
        setEditPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [showEdit, editForm?.policy_id, editForm?.date_of_birth, editForm?.gender, editForm?.date_of_joining, editForm?.enrollment_date, editForm?.employment_type]);

  const handleExtendFamilyWindow = async (e) => {
    e.preventDefault();
    if (!extendReason.trim()) {
      alert('Please provide a reason for extending the enrollment window.');
      return;
    }
    setExtendingWindow(true);
    setUpdateMessage(null);
    try {
      const { data } = await employeesAPI.extendFamilyWindow(id, 30, extendReason.trim());
      setUpdateMessage(data.message);
      setShowExtendModal(false);
      setExtendReason('');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to extend enrollment window');
    } finally {
      setExtendingWindow(false);
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    setEditError(null);
    setUpdateMessage(null);
    try {
      let payload;
      if (masterLocked) {
        payload = {
          policy_id: parseInt(editForm.policy_id, 10),
          enrollment_date: editForm.enrollment_date,
          family_enrollment_window_days: parseInt(editForm.family_enrollment_window_days, 10),
          enrollment_window_override_until: editForm.enrollment_window_override_until || null,
        };
      } else {
        payload = {
          ...editForm,
          department_id: parseInt(editForm.department_id, 10),
          branch_id: parseInt(editForm.branch_id, 10),
          policy_id: parseInt(editForm.policy_id, 10),
          salary_ctc: parseFloat(editForm.salary_ctc),
          family_enrollment_window_days: parseInt(editForm.family_enrollment_window_days, 10),
          enrollment_window_override_until: editForm.enrollment_window_override_until || null,
        };
        if (!payload.aadhaar) delete payload.aadhaar;
        if (!payload.pan) delete payload.pan;
      }
      if (user?.role === 'super_admin' && editForm.enrollment_window_override_until) {
        payload.enrollment_override_reason = editForm.enrollment_override_reason;
      }

      const { data } = await employeesAPI.update(id, payload);
      setShowEdit(false);
      setEditForm(null);
      setUpdateMessage(
        data.endorsementNumber
          ? `Details updated. Endorsement: ${data.endorsementNumber}`
          : 'Employee details updated successfully.'
      );
      load();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update employee');
    }
  };

  useEffect(() => {
    if (!showExit || !employee) return undefined;
    const timer = setTimeout(async () => {
      setExitLoading(true);
      try {
        const { data } = await employeesAPI.calculateExit(id, { exit_date: exitDate });
        setExitPreview(data.settlement);
      } catch {
        setExitPreview(null);
      } finally {
        setExitLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [showExit, exitDate, id, employee]);

  useEffect(() => {
    if (!showForm || !form.date_of_birth || !form.relation) {
      setFamilyPreview(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await familyAPI.calculatePremium({
          employee_id: parseInt(id, 10),
          relation: form.relation,
          date_of_birth: form.date_of_birth,
          gender: form.gender,
        });
        setFamilyPreview(data.premium);
      } catch {
        setFamilyPreview(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [showForm, form.date_of_birth, form.relation, form.gender, id]);

  const handleAddFamily = async (e) => {
    e.preventDefault();
    await familyAPI.create({ employee_id: parseInt(id, 10), ...form });
    setShowForm(false);
    setFamilyPreview(null);
    load();
  };

  const handleRemove = async (memberId) => {
    if (!confirm('Remove this dependent from coverage? Pro-rata refund will be calculated.')) return;
    const { data } = await familyAPI.remove(memberId);
    if (data.settlement?.refundTotal) {
      alert(`Dependent removed. Refund: ${formatCurrency(data.settlement.refundTotal)}`);
    }
    load();
  };

  const handleExit = async () => {
    const { data } = await employeesAPI.exit(id, { exit_date: exitDate });
    setShowExit(false);
    alert(`Exit processed. Refund: ${formatCurrency(data.refundAmount)}. Endorsement: ${data.endorsementNumber}`);
    load();
  };

  if (loading) return <LoadingSpinner />;
  if (!employee) return <p>Employee not found</p>;

  const dependents = (employee.familyMembers || []).filter((m) => m.relation !== 'self');
  const canDirectEditFamily = user?.role === 'super_admin' || !employee.has_portal_login;

  return (
    <div className="space-y-6">
      <Link to="/employees" className="text-primary-600 text-sm">← Back to employees</Link>

      {updateMessage && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">{updateMessage}</div>
      )}

      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{employee.full_name}</h1>
            <p className="text-slate-500">{employee.employee_code} · {employee.designation} · Age {employee.age}</p>
          </div>
          {canEdit && employee.enrollment_status !== 'exited' && (
            <div className="flex flex-wrap gap-2 self-start">
              <button type="button" onClick={openEdit} className="btn-secondary flex items-center gap-2">
                <Pencil className="w-4 h-4" />
                Edit Details
              </button>
              {user?.role === 'super_admin' && (
                <button
                  type="button"
                  onClick={() => setShowExtendModal(true)}
                  disabled={extendingWindow}
                  className="btn-primary text-sm"
                >
                  Extend Family Window (30 days)
                </button>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
          <div><span className="text-slate-400">Email</span><p>{employee.email}</p></div>
          <div><span className="text-slate-400">Mobile</span><p>{employee.mobile_number}</p></div>
          <div><span className="text-slate-400">Policy</span><p>{employee.policy_number || '—'}</p></div>
          <div><span className="text-slate-400">Enrollment Date</span><p>{employee.enrollment_date || '—'}</p></div>
          <div><span className="text-slate-400">Department</span><p>{employee.department_name}</p></div>
          <div><span className="text-slate-400">Employment</span><p className="capitalize">{employee.employment_type}</p></div>
          <div><span className="text-slate-400">Blood Group</span><p>{employee.blood_group}</p></div>
          <div><span className="text-slate-400">HRMS ID</span><p>{employee.hrms_integration_id || '—'}</p></div>
          <div><span className="text-slate-400">Status</span><p className="capitalize">{employee.enrollment_status}</p></div>
          <div><span className="text-slate-400">Family window (days)</span><p>{employee.family_enrollment_window_days ?? 10}</p></div>
          <div><span className="text-slate-400">Window override until</span><p>{employee.enrollment_window_override_until || '—'}</p></div>
          <div><span className="text-slate-400">Aadhaar</span><p>{employee.aadhaar_masked || '—'}</p></div>
          <div><span className="text-slate-400">Annual Premium (slab)</span><p>{formatCurrency(employee.annual_premium)}</p></div>
          <div><span className="text-slate-400">Pro-rata</span><p>{formatCurrency(employee.pro_rata_premium)}</p></div>
          <div><span className="text-slate-400">GST (18%)</span><p>{formatCurrency(employee.gst_amount)}</p></div>
          <div><span className="text-slate-400">Total</span><p className="font-semibold">{formatCurrency(employee.total_premium)}</p></div>
        </div>
        {employee.enrollment_status === 'enrolled' && (
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={async () => {
                await employeesAPI.collectPremium(id);
                load();
              }}
            >
              Mark Premium Collected
            </button>
            <button
              type="button"
              className="text-sm px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => setShowExit(true)}
            >
              Process Exit (Pro-rata Refund)
            </button>
          </div>
        )}
        {employee.enrollment_status === 'exited' && employee.exit_date && (
          <p className="mt-3 text-sm text-slate-500">Exited on {employee.exit_date}</p>
        )}
      </div>

      {showEdit && editForm && (
        <form onSubmit={handleUpdateEmployee} className="card space-y-4 border-primary-100">
          <h3 className="font-semibold text-lg">Update Employee &amp; Enrollment</h3>
          <p className="text-sm text-slate-500">Use this when an employee requests profile or enrollment corrections. Premium recalculates if DOB, policy, or enrollment date changes.</p>
          {masterLocked && (
            <div className="p-3 bg-sky-50 text-sky-900 rounded text-sm">
              HRMS sync is enabled — identity/org/CTC fields are read-only. Update policy, enrollment date, and family window only.
            </div>
          )}
          {editError && <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{editError}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="label">Employee Code</label>
              <input className="input-field" required disabled={masterLocked} value={editForm.employee_code} onChange={(e) => setEditForm({ ...editForm, employee_code: e.target.value })} />
            </div>
            <div>
              <label className="label">Full Name *</label>
              <input className="input-field" required disabled={masterLocked} value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} />
            </div>
            <div>
              <label className="label">Designation *</label>
              <input className="input-field" required disabled={masterLocked} value={editForm.designation} onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })} />
            </div>
            <div>
              <label className="label">Email *</label>
              <input type="email" className="input-field" required disabled={masterLocked} value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div>
              <label className="label">Mobile *</label>
              <input className="input-field" required disabled={masterLocked} value={editForm.mobile_number} onChange={(e) => setEditForm({ ...editForm, mobile_number: e.target.value })} />
            </div>
            <div>
              <label className="label">Date of Birth *</label>
              <input type="date" className="input-field" required disabled={masterLocked} value={editForm.date_of_birth} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} />
            </div>
            <div>
              <label className="label">Date of Joining *</label>
              <input type="date" className="input-field" required disabled={masterLocked} value={editForm.date_of_joining} onChange={(e) => setEditForm({ ...editForm, date_of_joining: e.target.value })} />
            </div>
            <div>
              <label className="label">Enrollment Date *</label>
              <input type="date" className="input-field" required value={editForm.enrollment_date} onChange={(e) => setEditForm({ ...editForm, enrollment_date: e.target.value })} />
            </div>
            <div>
              <label className="label">Gender *</label>
              <select className="input-field" disabled={masterLocked} value={editForm.gender} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="label">Blood Group *</label>
              <select className="input-field" disabled={masterLocked} value={editForm.blood_group} onChange={(e) => setEditForm({ ...editForm, blood_group: e.target.value })}>
                {BLOOD_GROUPS.map((bg) => <option key={bg} value={bg}>{bg}</option>)}
              </select>
            </div>
            <div>
              <label className="label">CTC (INR) *</label>
              <input type="number" className="input-field" required disabled={masterLocked} value={editForm.salary_ctc} onChange={(e) => setEditForm({ ...editForm, salary_ctc: e.target.value })} />
            </div>
            <div>
              <label className="label">Department *</label>
              <select className="input-field" required disabled={masterLocked} value={editForm.department_id} onChange={(e) => setEditForm({ ...editForm, department_id: e.target.value })}>
                <option value="">Select</option>
                {lookups.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Branch *</label>
              <select className="input-field" required disabled={masterLocked} value={editForm.branch_id} onChange={(e) => setEditForm({ ...editForm, branch_id: e.target.value })}>
                <option value="">Select</option>
                {lookups.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Policy *</label>
              <select className="input-field" required value={editForm.policy_id} onChange={(e) => setEditForm({ ...editForm, policy_id: e.target.value })}>
                <option value="">Select</option>
                {lookups.policies.map((p) => <option key={p.id} value={p.id}>{p.company_name} - {p.policy_number}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Employment Type *</label>
              <select className="input-field" disabled={masterLocked} value={editForm.employment_type} onChange={(e) => setEditForm({ ...editForm, employment_type: e.target.value })}>
                <option value="permanent">Permanent</option>
                <option value="contract">Contract</option>
                <option value="probation">Probation</option>
              </select>
            </div>
            <div>
              <label className="label">HRMS Integration ID</label>
              <input className="input-field" disabled={masterLocked} value={editForm.hrms_integration_id} onChange={(e) => setEditForm({ ...editForm, hrms_integration_id: e.target.value })} />
            </div>
            <div>
              <label className="label">New Aadhaar (optional)</label>
              <input className="input-field" maxLength={12} disabled={masterLocked} value={editForm.aadhaar} onChange={(e) => setEditForm({ ...editForm, aadhaar: e.target.value })} placeholder={employee.aadhaar_masked ? 'Leave blank to keep current' : ''} />
            </div>
            <div>
              <label className="label">New PAN (optional)</label>
              <input className="input-field" maxLength={10} disabled={masterLocked} value={editForm.pan} onChange={(e) => setEditForm({ ...editForm, pan: e.target.value })} />
            </div>
            {user?.role === 'super_admin' && (
              <>
                <div>
                  <label className="label">Family enrollment window (days)</label>
                  <input type="number" min={1} className="input-field" value={editForm.family_enrollment_window_days} onChange={(e) => setEditForm({ ...editForm, family_enrollment_window_days: e.target.value })} />
                </div>
                <div>
                  <label className="label">Enrollment window override until</label>
                  <input type="date" className="input-field" value={editForm.enrollment_window_override_until} onChange={(e) => setEditForm({ ...editForm, enrollment_window_override_until: e.target.value })} />
                </div>
                <div>
                  <label className="label">Override reason (required if setting date)</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. Employee requested extension for spouse enrollment"
                    value={editForm.enrollment_override_reason}
                    onChange={(e) => setEditForm({ ...editForm, enrollment_override_reason: e.target.value })}
                  />
                </div>
              </>
            )}
          </div>

          {(editPreview || editPreviewLoading || editError) && (
            <div className="p-4 bg-primary-50 border border-primary-100 rounded-lg text-sm">
              <h4 className="font-semibold text-primary-900 mb-2">Premium Preview (if enrollment fields change)</h4>
              {editPreviewLoading && <p className="text-slate-500">Calculating…</p>}
              {editPreview && !editPreviewLoading && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><span className="text-slate-500 block">Age slab</span><span className="font-medium">{editPreview.slabInfo?.ageBand || '—'}</span></div>
                  <div><span className="text-slate-500 block">Annual</span><span className="font-medium">{formatCurrency(editPreview.annualPremium)}</span></div>
                  <div><span className="text-slate-500 block">Pro-rata</span><span className="font-medium">{formatCurrency(editPreview.proRataPremium)}</span></div>
                  <div><span className="text-slate-500 block">Total</span><span className="font-semibold text-primary-700">{formatCurrency(editPreview.totalPremium)}</span></div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" className="btn-primary">Save Changes</button>
            <button type="button" className="btn-secondary" onClick={() => { setShowEdit(false); setEditForm(null); }}>Cancel</button>
          </div>
        </form>
      )}

      {showExit && (
        <div className="card border-red-100">
          <h3 className="font-semibold text-lg mb-3">Exit Settlement Preview</h3>
          <div className="max-w-xs mb-3">
            <label className="label">Exit Date</label>
            <input
              type="date"
              className="input-field"
              value={exitDate}
              onChange={(e) => setExitDate(e.target.value)}
            />
          </div>
          {exitLoading && <p className="text-sm text-slate-500">Calculating refund from age slabs…</p>}
          {!exitLoading && exitPreview && (
            <SettlementPanel settlement={exitPreview} title="Age-slab based pro-rata settlement" />
          )}
          <div className="flex gap-2 mt-4">
            <button type="button" className="btn-primary bg-red-600 hover:bg-red-700" onClick={handleExit} disabled={exitLoading || !exitPreview}>
              Confirm Exit
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowExit(false)}>Cancel</button>
          </div>
        </div>
      )}

      {termCoverages.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Term Life Coverage</h2>
          <div className="space-y-3">
            {termCoverages.map((tc) => (
              <div key={tc.id} className="p-4 bg-rose-50 rounded-lg border border-rose-100 text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><span className="text-slate-400 block">Policy</span>{tc.policy_number}</div>
                <div><span className="text-slate-400 block">Sum Assured</span><strong>{formatCurrency(tc.sum_assured)}</strong></div>
                <div><span className="text-slate-400 block">Premium</span>{formatCurrency(tc.total_premium)}</div>
                <div><span className="text-slate-400 block">Status</span><span className="capitalize">{tc.status}</span></div>
                <div><span className="text-slate-400 block">Nominee</span>{tc.nominee_name || '—'} {tc.nominee_relation ? `(${tc.nominee_relation})` : ''}</div>
                <div><span className="text-slate-400 block">Provider</span>{tc.provider_name}</div>
                <div><span className="text-slate-400 block">Enrolled</span>{tc.enrollment_date}</div>
                <div><span className="text-slate-400 block">Mode</span><span className="capitalize">{tc.premium_mode}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg font-semibold">Family Coverage (1 + 5)</h2>
            <p className="text-sm text-slate-500">{dependents.length}/5 dependents added</p>
            {employee.has_portal_login && user?.role !== 'super_admin' && (
              <p className="text-sm text-amber-700 mt-2">
                This employee uses the self-service portal. Family changes must be submitted via employee Add Family and approved in Approvals.
              </p>
            )}
          </div>
          {dependents.length < 5 && employee.enrollment_status === 'enrolled' && canDirectEditFamily && (
            <button className="btn-primary" onClick={() => setShowForm(!showForm)}>Add Dependent</button>
          )}
        </div>

        {showForm && (
          <form onSubmit={handleAddFamily} className="mb-6 p-4 bg-slate-50 rounded-lg grid md:grid-cols-3 gap-4">
            <div><label className="label">Name</label><input className="input-field" required value={form.member_name} onChange={(e) => setForm({ ...form, member_name: e.target.value })} /></div>
            <div>
              <label className="label">Relation</label>
              <select className="input-field" value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })}>
                {RELATIONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div><label className="label">DOB</label><input type="date" className="input-field" required value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
            <div>
              <label className="label">Gender</label>
              <select className="input-field" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
              </select>
            </div>
            <div><label className="label">Aadhaar (KYC)</label><input className="input-field" maxLength={12} value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} /></div>
            <div><label className="label">Pre-existing Disease</label><input className="input-field" value={form.pre_existing_disease} onChange={(e) => setForm({ ...form, pre_existing_disease: e.target.value })} /></div>
            {familyPreview && (
              <div className="md:col-span-3 p-3 bg-primary-50 rounded text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>Age: <strong>{familyPreview.age}</strong></div>
                <div>Slab: <strong>{familyPreview.slabInfo?.ageBand}</strong></div>
                <div>Pro-rata: <strong>{formatCurrency(familyPreview.proRataPremium)}</strong></div>
                <div>Total: <strong>{formatCurrency(familyPreview.totalPremium)}</strong></div>
              </div>
            )}
            <div className="md:col-span-3"><button type="submit" className="btn-primary">Save &amp; Calculate Premium</button></div>
          </form>
        )}

        <table className="w-full text-sm">
          <thead><tr className="text-slate-500 border-b"><th className="pb-2 text-left">Member</th><th>Relation</th><th>Age</th><th>Premium</th><th>PED</th><th></th></tr></thead>
          <tbody>
            {(employee.familyMembers || []).map((m) => (
              <tr key={m.id} className="border-b border-slate-100">
                <td className="py-2">{m.member_name}</td>
                <td className="capitalize">{m.relation?.replace(/_/g, ' ')}</td>
                <td>{m.age}</td>
                <td>{formatCurrency(parseFloat(m.pro_rata_premium) + parseFloat(m.gst_amount))}</td>
                <td>{m.pre_existing_disease || '—'}</td>
                <td>{m.relation !== 'self' && employee.enrollment_status === 'enrolled' && canDirectEditFamily && (
                  <button onClick={() => handleRemove(m.id)} className="text-red-600 text-xs">Remove</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showExtendModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleExtendFamilyWindow} className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold">Extend Family Enrollment Window</h3>
            <p className="text-sm text-slate-500">Opens the employee self-service family enrollment window for 30 more days.</p>
            <div>
              <label className="label">Reason *</label>
              <textarea
                className="input-field min-h-[80px]"
                required
                value={extendReason}
                onChange={(e) => setExtendReason(e.target.value)}
                placeholder="e.g. Employee missed the initial enrollment window"
              />
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary" disabled={extendingWindow}>
                {extendingWindow ? 'Saving…' : 'Extend 30 Days'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => { setShowExtendModal(false); setExtendReason(''); }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
